import React from "react";
import { Link } from "react-router-dom";
import { PageHero, SectionHeader, SiteLayout } from "./components/SiteLayout";
import { aboutApproach, aboutStory, impactStats, industries, leadership } from "./siteData";

export default function AboutPage() {
  return (
    <SiteLayout title="About" description="About SaskTech Lab and the people behind the work.">
      <PageHero
        eyebrow="About SaskTech Lab"
        title="Bridging the gap between education and industry through innovative engineering solutions."
        description="SaskTech Lab exists to solve real client problems while helping early-career engineers gain the kind of experience that usually takes years to access."
        actions={[
          <Link key="contact" to="/contact" className="button-tertiary">
            Talk to our team
          </Link>,
        ]}
      />

      {/* Our Story */}
      <section className="site-shell section-space">
        <SectionHeader eyebrow="Our Story" title="How SaskTech Lab came to be." description="Founded to solve the experience gap that holds back talented engineering graduates." />
        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          {aboutStory.map((paragraph, index) => (
            <div key={index} className="panel-card text-base leading-8 text-slate-600">
              {paragraph}
            </div>
          ))}
        </div>
      </section>

      {/* Our Approach */}
      <section className="site-shell section-space">
        <SectionHeader eyebrow="Our Approach" title="A dual-purpose business model that creates value on all sides." description="We combine commercial engineering services with structured talent development." />
        <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {aboutApproach.map((item) => (
            <article key={item.title} className="panel-card h-full">
              <h3 className="font-display text-2xl font-bold text-emerald-950">{item.title}</h3>
              <p className="mt-4 text-sm leading-7 text-slate-600">{item.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Industries */}
      <section className="site-shell section-space">
        <SectionHeader eyebrow="Industries" title="The company is strongest where operational reliability actually matters." description="Sectors where our engineering work has practical relevance and real impact." />
        <div className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {industries.map((industry) => (
            <article key={industry.name} className="panel-card">
              <h3 className="font-display text-2xl font-bold text-emerald-950">{industry.name}</h3>
              <p className="mt-4 text-sm leading-7 text-slate-600">{industry.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Team */}
      <section className="site-shell section-space">
        <SectionHeader eyebrow="Our Team" title="Meet the people behind SaskTech Lab." description="A cross-disciplinary team of engineers, designers, and professional specialists." />
        <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {leadership.map((person) => (
            <article key={person.name} className="panel-card h-full flex flex-col">
              <div className="mb-4 overflow-hidden rounded-2xl bg-emerald-50 aspect-[4/3]">
                <img
                  src={person.photo}
                  alt={person.name}
                  className="h-full w-full object-cover object-top"
                  onError={(e) => { e.target.src = "/faces/default.jpg"; }}
                />
              </div>
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-emerald-700">{person.role}</p>
              <h3 className="mt-2 font-display text-xl font-bold text-emerald-950">
                <a href={person.linkedin} target="_blank" rel="noopener noreferrer" className="hover:text-emerald-700 transition-colors">
                  {person.name}
                </a>
              </h3>
              <p className="mt-1 text-xs text-slate-500 italic">{person.degree}</p>
              <p className="mt-3 text-sm leading-7 text-slate-600 flex-1">{person.bio}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Impact Stats */}
      <section className="site-shell section-space">
        <SectionHeader eyebrow="Our Impact" title="Growing our impact every year." description="Numbers that reflect our commitment to quality engineering and talent development." />
        <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {impactStats.map((stat) => (
            <div key={stat.label} className="panel-card text-center">
              <p className="font-display text-5xl font-bold text-emerald-900">{stat.value}</p>
              <p className="mt-3 text-base text-slate-600">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Join Our Mission */}
      <section className="site-shell section-space pb-10">
        <div className="rounded-[36px] border border-emerald-900/10 bg-gradient-to-br from-white via-emerald-50 to-emerald-100 p-8 lg:p-10">
          <SectionHeader eyebrow="Join Our Mission" title="Be part of our journey to transform engineering education and practice." description="" />
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/contact" className="button-tertiary">Work With Us</Link>
            <Link to="/careers" className="button-secondary">Join Our Team</Link>
            <Link to="/contact" className="button-secondary">Become a Mentor</Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}